import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronDown,
  Eye,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  RotateCw,
  Smartphone,
  TicketX,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import type { INotificationStatusRow, NotificationChannel } from '@/lib/notifications/types';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { toneClasses, type BoardTone } from './airport-tokens';
import type { CardClock } from './card-time';
import { CallbackRequest } from './callback-request';
import { channelChips, conversationLines, type ITalkChip, type ITalkLine, type TalkKind } from './conversation-model';
import { CardAction, ServiceCard } from './pnr-card-ui';
import { PnrNotificationStatus } from './pnr-notification-status';
import { callbackRequested } from './pnr-steps';

// The conversation with the passenger, in view on the card (PO 2026-10-31; redrawn 2026-11-03:
// «es imposible de leer… del más reciente al más viejo y un ver más… iconos, colores»). The
// newest lines first, three of them, «Ver N más» for the rest in place and «Ver todo» for the
// whole conversation in a dialog — each line an icon in the colour of who acted: grey what we
// sent, indigo what the passenger did, green their yes, red what failed or their no.

/** How many lines the card shows before «Ver N más». */
export const TALK_VISIBLE = 3;

const CHANNEL_ICON: Record<NotificationChannel, LucideIcon> = {
  EMAIL: Mail,
  SMS: MessageSquare,
  WHATSAPP: MessageCircle,
  VOICE: Phone,
  PUSH: Smartphone,
};

const KIND_TONE: Record<TalkKind, BoardTone> = {
  sent: 'idle',
  passenger: 'info',
  accepted: 'ok',
  declined: 'alert',
  failed: 'alert',
  notBought: 'alert',
};

function lineIcon(line: ITalkLine): LucideIcon {
  if (line.kind === 'failed') return TriangleAlert;
  if (line.kind === 'passenger') return Eye;
  if (line.kind === 'accepted') return Check;
  if (line.kind === 'declined') return X;
  if (line.kind === 'notBought') return TicketX;
  return line.channel ? CHANNEL_ICON[line.channel] : Mail;
}

/** The lines under their day: «Hoy», «2 nov». */
function byDay(lines: readonly ITalkLine[], clock: CardClock, today: string): Array<{ day: string; lines: ITalkLine[] }> {
  const days: Array<{ day: string; lines: ITalkLine[] }> = [];
  for (const l of lines) {
    const day = clock.isToday(l.at) ? today : clock.date(l.at);
    const last = days[days.length - 1];
    if (last && last.day === day) last.lines.push(l);
    else days.push({ day, lines: [l] });
  }
  return days;
}

function TalkLine({
  line,
  clock,
  rail,
  full,
  onContact,
}: {
  line: ITalkLine;
  clock: CardClock;
  /** A line follows on the same day: the rail joins their icons. */
  rail: boolean;
  /** The dialog: every repeat's time and each receipt's. */
  full: boolean;
  onContact?: (() => void) | undefined;
}) {
  const { t } = useTranslation('pnr');
  const tone = toneClasses(KIND_TONE[line.kind]);
  const Icon = lineIcon(line);
  const first = line.times[0] ?? line.at;
  const repeats = line.times.length;
  const when =
    repeats > 1 && first !== line.at
      ? `${clock.date(first) === clock.date(line.at) ? clock.time(first) : clock.moment(first)} – ${clock.time(line.at)}`
      : clock.time(line.at);
  return (
    <li className="relative flex gap-3 pb-4 last:pb-1" data-testid="pnr-talk-line" data-kind={line.kind}>
      {rail ? <span aria-hidden className="absolute bottom-0.5 left-[15px] top-9 w-0.5 rounded bg-border" /> : null}
      <span
        aria-hidden
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border',
          tone.surface,
          tone.border,
          line.kind === 'sent' ? 'text-muted-foreground' : tone.text,
        )}
      >
        <Icon className="h-4 w-4" strokeWidth={line.kind === 'accepted' ? 2.5 : 2} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1 pt-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <span
            className={cn(
              'text-sm font-semibold leading-snug',
              line.kind !== 'sent' && line.kind !== 'passenger' && tone.text,
            )}
          >
            {line.text}
            {repeats > 1 ? (
              <span
                className={cn('ml-2 rounded-full px-1.5 py-px text-xs font-bold', tone.chip)}
                data-testid="pnr-talk-repeats"
              >
                ×{repeats}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">{when}</span>
        </div>
        {line.detail ? <span className="text-[13px] leading-snug text-muted-foreground">{line.detail}</span> : null}
        {line.steps.length > 0 ? (
          <span className="flex flex-wrap gap-1.5">
            {line.steps.map((s) => (
              <span
                key={s.label}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-px text-xs font-medium',
                  s.done ? toneClasses('ok').chip : toneClasses('idle').chip,
                )}
              >
                {s.done ? <Check className="h-3 w-3" strokeWidth={3} aria-hidden /> : null}
                {s.label}
                {full && s.at ? <span className="tabular-nums opacity-80">· {clock.time(s.at)}</span> : null}
              </span>
            ))}
          </span>
        ) : null}
        {full && repeats > 1 ? (
          <span className="text-xs tabular-nums text-muted-foreground" data-testid="pnr-talk-times">
            {line.times.map((at) => clock.time(at)).join(' · ')}
          </span>
        ) : null}
        {line.missingContact && onContact ? (
          <button
            type="button"
            className="self-start text-[13px] font-semibold text-primary underline-offset-2 hover:underline"
            onClick={onContact}
            data-testid="pnr-talk-contact"
          >
            {t('card.talk.updateContact')}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function TalkLines({
  lines,
  clock,
  full = false,
  onContact,
}: {
  lines: readonly ITalkLine[];
  clock: CardClock;
  full?: boolean;
  onContact?: (() => void) | undefined;
}) {
  const { t } = useTranslation('pnr');
  return (
    <div className="flex flex-col gap-1" data-testid="pnr-talk-lines">
      {byDay(lines, clock, t('card.talk.today')).map((d) => (
        <section key={d.day} aria-label={d.day} className="flex flex-col gap-2">
          <h4 className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{d.day}</h4>
          <ol className="flex flex-col">
            {d.lines.map((l, i) => (
              <TalkLine
                key={`${l.kind}-${l.at}-${i}`}
                line={l}
                clock={clock}
                rail={i < d.lines.length - 1}
                full={full}
                onContact={onContact}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function Chips({ chips }: { chips: readonly ITalkChip[] }) {
  if (chips.length === 0) return null;
  return (
    <p className="flex flex-wrap gap-1.5" data-testid="pnr-talk-channels">
      {chips.map((c) => {
        const Icon = c.channel ? CHANNEL_ICON[c.channel] : null;
        return (
          <span
            key={c.key}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset',
              toneClasses(c.tone).chip,
            )}
            data-tone={c.tone}
          >
            {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
            {c.label}
          </span>
        );
      })}
    </p>
  );
}

export function PnrConversation({
  group,
  notification,
  clock,
  onResend,
  resendPending,
  onCall,
  onContact,
  forceAccept,
  answerFlight,
  purchaseFailed,
}: {
  group: DisruptionPnrGroupRow;
  notification: INotificationStatusRow | null | undefined;
  /** The station's clock (card-time.ts). */
  clock: CardClock;
  /** The flight the passenger's last answer was about (`answeredFlight`). */
  answerFlight?: string | undefined;
  /** Nexa's purchase failed after they accepted: when, and why in words. */
  purchaseFailed?: { at: string; why?: string | undefined } | undefined;
  onResend: () => void;
  resendPending: boolean;
  /** «Prefiero que me llamen»: call them / fix the contact (the card owns both). */
  onCall?: (() => void) | undefined;
  onContact?: (() => void) | undefined;
  /** While the offer awaits: the supervisor's «accept on their behalf» (the card owns it). */
  forceAccept?: ReactNode;
}) {
  const { t } = useTranslation('pnr');
  const tr = (k: string, o?: Record<string, unknown>): string => String(t(k, o ?? {}));
  // Opened for THIS passenger: the next one in the drawer starts folded again.
  const [expandedFor, setExpandedFor] = useState<string | null>(null);
  const [allOpen, setAllOpen] = useState(false);
  const answer = group.lastOfferResponse;
  // A callback request has its own attention line above; the other answers are lines.
  const lines = conversationLines({
    row: notification,
    answer: answer && !callbackRequested(group) ? answer : undefined,
    answerFlight,
    purchaseFailed,
    t: tr,
  });
  const chips = channelChips({
    sends: notification?.sends ?? [],
    contact: group.contactOnFile ?? group.passengers[0]?.contactOnFile,
    t: tr,
  });
  const expanded = expandedFor === group.subCaseUrn;
  const shown = expanded ? lines : lines.slice(0, TALK_VISIBLE);
  const hidden = lines.length - TALK_VISIBLE;
  // Their yes is green only while nothing went wrong after it.
  const status =
    answer && !callbackRequested(group)
      ? answer.response === 'ACCEPTED' && purchaseFailed
        ? { text: t('card.talk.status.acceptedNotBought'), tone: 'alert' as const }
        : {
            text: t(answer.response === 'ACCEPTED' ? 'card.talk.status.accepted' : 'card.talk.status.declined', {
              time: clock.moment(answer.at),
            }),
            tone: answer.response === 'ACCEPTED' ? ('ok' as const) : ('alert' as const),
          }
      : null;

  return (
    <ServiceCard
      title={t('card.talk.title')}
      testId="pnr-card-conversation"
      status={status?.text}
      statusTone={status?.tone ?? 'idle'}
      actions={
        <>
          <CardAction onClick={onResend} disabled={resendPending} testId="pnr-talk-resend">
            <RotateCw className="h-3.5 w-3.5" aria-hidden />
            {t('card.talk.resend')}
          </CardAction>
          {forceAccept}
          {lines.length > 0 ? (
            <CardAction onClick={() => setAllOpen(true)} testId="pnr-open-conversation">
              {t('card.talk.open')}
            </CardAction>
          ) : null}
        </>
      }
    >
      <CallbackRequest
        group={group}
        formatTime={(iso) => clock.moment(iso)}
        onCall={onCall}
        onContact={onContact}
        calling={resendPending}
      />
      <Chips chips={chips} />
      <PnrNotificationStatus
        caseUrn={group.caseUrn}
        subCaseUrn={group.subCaseUrn}
        inline
        answered={!!answer}
        transientOnly
      />
      {lines.length > 0 ? (
        <>
          <TalkLines lines={shown} clock={clock} onContact={onContact} />
          {hidden > 0 ? (
            <button
              type="button"
              className="inline-flex min-h-9 items-center gap-1.5 self-start rounded-full bg-muted px-3.5 text-[13px] font-semibold text-foreground/80 hover:bg-accent"
              onClick={() => setExpandedFor(expanded ? null : group.subCaseUrn)}
              aria-expanded={expanded}
              data-testid="pnr-talk-more"
            >
              {expanded ? t('card.talk.less') : t('card.talk.more', { count: hidden })}
              <ChevronDown className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')} aria-hidden />
            </button>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="pnr-talk-empty">
          {t('card.talk.empty')}
        </p>
      )}
      <Dialog open={allOpen} onOpenChange={setAllOpen}>
        <DialogContent className="z-[60] flex max-h-[85vh] max-w-xl flex-col" data-testid="pnr-talk-dialog">
          <DialogHeader>
            <DialogTitle>{t('card.talk.allTitle', { pnr: group.pnrLabel })}</DialogTitle>
            <DialogDescription className="sr-only">{t('card.talk.allDescription')}</DialogDescription>
          </DialogHeader>
          <Chips chips={chips} />
          <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
            <TalkLines lines={lines} clock={clock} full onContact={onContact} />
          </div>
        </DialogContent>
      </Dialog>
    </ServiceCard>
  );
}
